"use server"

import { sdk } from "@lib/config"
import {
  CheckoutFieldErrors,
  cleanCheckoutFields,
  normalizeIraqiPhone,
  splitFullName,
  validateCheckoutFields,
} from "@lib/util/iraq-checkout"
import medusaError from "@lib/util/medusa-error"
import { HttpTypes } from "@medusajs/types"
import { FetchError } from "@medusajs/js-sdk"
import { revalidateTag } from "next/cache"
import { redirect } from "next/navigation"
import {
  getAuthHeaders,
  getCacheOptions,
  getCacheTag,
  getCartId,
  getPendingCustomer,
  removeAuthToken,
  removeCartId,
  removePendingCustomer,
  setAuthToken,
  setPendingCustomer,
} from "./cookies"

export type CustomerAuthState =
  | { state: "error"; error: string }
  | { state: "verification_required"; email: string }
  | { state: "success" }
  | null

const GENERIC_ERROR = "تعذّر إتمام العملية. حاولي مرة أخرى بعد قليل."
const WRONG_LOGIN = "البريد الإلكتروني أو كلمة المرور غير صحيحة."
const ALREADY_MEMBER =
  "هذا البريد الإلكتروني مسجّل من قبل. سجّلي الدخول بكلمة مروركِ."

/** Turns the backend's English errors into a message the customer can act on. */
function arabicAuthError(error: unknown, when: "login" | "signup"): string {
  const text = String(
    (error as Error)?.message ?? error ?? ""
  ).toLowerCase()

  if (text.includes("invalid email or password")) {
    return when === "signup" ? ALREADY_MEMBER : WRONG_LOGIN
  }

  if (text.includes("already")) {
    return ALREADY_MEMBER
  }

  return GENERIC_ERROR
}

// Requests a verification email for the given customer. The request must be
// authenticated with a token tied to the auth identity (the token returned by
// register or by a login that requires verification).
async function requestVerificationEmail(email: string, token: string) {
  await sdk.auth.verification.request(
    {
      entity_id: email,
      entity_type: "email",
    },
    {
      authorization: `Bearer ${token}`,
    }
  )
}

export const retrieveCustomer =
  async (): Promise<HttpTypes.StoreCustomer | null> => {
    const authHeaders = await getAuthHeaders()

    if (!authHeaders) return null

    const headers = {
      ...authHeaders,
    }

    const next = {
      ...(await getCacheOptions("customers")),
    }

    return await sdk.client
      .fetch<{ customer: HttpTypes.StoreCustomer }>(`/store/customers/me`, {
        method: "GET",
        query: {
          fields: "*orders",
        },
        headers,
        next,
        cache: "force-cache",
      })
      .then(({ customer }) => customer)
      .catch(() => null)
  }

export const updateCustomer = async (body: HttpTypes.StoreUpdateCustomer) => {
  const headers = {
    ...(await getAuthHeaders()),
  }

  const updateRes = await sdk.store.customer
    .update(body, {}, headers)
    .then(({ customer }) => customer)
    .catch(medusaError)

  const cacheTag = await getCacheTag("customers")
  revalidateTag(cacheTag)

  return updateRes
}

export async function signup(
  _currentState: unknown,
  formData: FormData
): Promise<CustomerAuthState> {
  const read = (name: string) => String(formData.get(name) ?? "").trim()
  const password = String(formData.get("password") ?? "")
  const fullName = read("full_name").replace(/\s+/g, " ")
  const phone = normalizeIraqiPhone(read("phone"))
  const email = read("email").toLowerCase()

  // The form checks these too. This is for requests that skip the form.
  if (fullName.length < 3) {
    return { state: "error", error: "اكتبي اسمكِ الكامل." }
  }
  if (!phone) {
    return {
      state: "error",
      error: "اكتبي رقم هاتف عراقي صحيح، مثل 07701234567.",
    }
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { state: "error", error: "اكتبي بريداً إلكترونياً صحيحاً." }
  }
  if (password.length < 8) {
    return {
      state: "error",
      error: "كلمة المرور قصيرة. اختاري ٨ أحرف أو أرقام على الأقل.",
    }
  }

  const customerForm = { email, ...splitFullName(fullName), phone }

  try {
    await sdk.auth.register("customer", "emailpass", {
      email: customerForm.email,
      password,
    })
  } catch (error) {
    const fetchError = error as FetchError
    // An existing identity (for example, an admin user with the same email) is
    // expected and handled: the customer can still log in to link a customer
    // record. Any other error is surfaced.
    if (
      fetchError.statusText !== "Unauthorized" ||
      fetchError.message !== "Identity with email already exists"
    ) {
      return { state: "error", error: arabicAuthError(error, "signup") }
    }
  }

  // Persist the extra signup fields. The customer record is created during
  // login, which is deferred until after email verification when the backend
  // requires it.
  await setPendingCustomer(customerForm)

  // Continue by logging in. The login response tells us whether the backend
  // requires email verification — we don't need a storefront-side flag.
  return completeLogin(customerForm.email, password, "signup")
}

export async function login(
  _currentState: unknown,
  formData: FormData
): Promise<CustomerAuthState> {
  const email = String(formData.get("email") ?? "")
    .trim()
    .toLowerCase()
  const password = String(formData.get("password") ?? "")

  if (!email || !password) {
    return { state: "error", error: WRONG_LOGIN }
  }

  return completeLogin(email, password, "login")
}

// Logs the customer in and reconciles the customer record. The behavior is
// driven entirely by the backend's login response, so it works whether or not
// email verification is enabled.
async function completeLogin(
  email: string,
  password: string,
  when: "login" | "signup"
): Promise<CustomerAuthState> {
  let result: Awaited<ReturnType<typeof sdk.auth.login>>

  try {
    result = await sdk.auth.login("customer", "emailpass", { email, password })
  } catch (error) {
    return { state: "error", error: arabicAuthError(error, when) }
  }

  // A `location` is returned by third-party auth providers, which this flow
  // doesn't support.
  if (typeof result === "object" && "location" in result) {
    return {
      state: "error",
      error: GENERIC_ERROR,
    }
  }

  // The backend requires email verification and the customer hasn't verified
  // yet. Send the verification email and ask them to check their inbox.
  if (
    typeof result === "object" &&
    "verification_required" in result &&
    result.verification_required
  ) {
    try {
      await requestVerificationEmail(email, result.token)
    } catch {
      // Ignore: the customer can resend from the verification page.
    }
    return { state: "verification_required", email }
  }

  if (typeof result !== "string") {
    return {
      state: "error",
      error: GENERIC_ERROR,
    }
  }

  let token = result

  // The token may not be tied to a customer record yet — right after
  // registration, or after verifying a brand-new account. Ask the backend:
  // `/store/customers/me` rejects tokens without a registered actor, so a
  // failed retrieve means we still need to create the customer, then log in
  // again to obtain a customer-bound token.
  const customerExists = await sdk.store.customer
    .retrieve({}, { authorization: `Bearer ${token}` })
    .then(() => true)
    .catch(() => false)

  if (!customerExists) {
    const pending = await getPendingCustomer()

    try {
      await sdk.store.customer.create(
        {
          email,
          first_name: pending?.first_name,
          last_name: pending?.last_name,
          phone: pending?.phone,
        },
        {},
        { authorization: `Bearer ${token}` }
      )

      token = (await sdk.auth.login("customer", "emailpass", {
        email,
        password,
      })) as string
    } catch (error) {
      return { state: "error", error: arabicAuthError(error, when) }
    }

    await removePendingCustomer()
  }

  await setAuthToken(token)

  const customerCacheTag = await getCacheTag("customers")
  revalidateTag(customerCacheTag)

  // Signing in must not fail because the cart could not follow the customer.
  try {
    await transferCart()
  } catch {
    // The cart stays as it was.
  }

  return { state: "success" }
}

// Confirms a customer's email using the token from the verification link.
//
// The confirm route doesn't require authentication, so this works even when the
// customer opens the link on a different device than the one they signed up on.
export async function confirmEmailVerification(
  token: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await sdk.auth.verification.confirm({ code: token })
    return { success: true }
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

export async function signout(countryCode: string) {
  await sdk.auth.logout()

  await removeAuthToken()

  const customerCacheTag = await getCacheTag("customers")
  revalidateTag(customerCacheTag)

  await removeCartId()

  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag)

  redirect(`/${countryCode}/account`)
}

export async function transferCart() {
  const cartId = await getCartId()

  if (!cartId) {
    return
  }

  const headers = await getAuthHeaders()

  await sdk.store.cart.transferCart(cartId, {}, headers)

  const cartCacheTag = await getCacheTag("carts")
  revalidateTag(cartCacheTag)
}

export type AddressState = {
  success: boolean
  error: string | null
  /** Shown under the field each one belongs to. */
  fields?: CheckoutFieldErrors
}

/**
 * Reads the address form (name, phone, governorate, area, address) and checks
 * it with the same rules as the checkout, so a saved address always works there.
 */
function readAddressForm(formData: FormData) {
  const read = (name: string) => String(formData.get(name) ?? "")
  const fields = cleanCheckoutFields({
    full_name: read("full_name"),
    phone: read("phone"),
    province: read("province"),
    city: read("city"),
    address: read("address"),
    notes: "",
    email: "",
  })
  const errors = validateCheckoutFields(fields)

  return {
    errors,
    address: {
      ...splitFullName(fields.full_name),
      phone: normalizeIraqiPhone(fields.phone) ?? fields.phone,
      province: fields.province,
      city: fields.city,
      address_1: fields.address,
      country_code: "iq",
    },
  }
}

const ADDRESS_FAILED = "تعذّر حفظ العنوان. حاولي مرة أخرى بعد قليل."

export const addCustomerAddress = async (
  _currentState: AddressState,
  formData: FormData
): Promise<AddressState> => {
  const { errors, address } = readAddressForm(formData)

  if (Object.keys(errors).length) {
    return { success: false, error: null, fields: errors }
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.customer
    .createAddress(address, {}, headers)
    .then(async () => {
      const customerCacheTag = await getCacheTag("customers")
      revalidateTag(customerCacheTag)
      return { success: true, error: null }
    })
    .catch(() => {
      return { success: false, error: ADDRESS_FAILED }
    })
}

export const deleteCustomerAddress = async (
  addressId: string
): Promise<void> => {
  const headers = {
    ...(await getAuthHeaders()),
  }

  await sdk.store.customer
    .deleteAddress(addressId, headers)
    .then(async () => {
      const customerCacheTag = await getCacheTag("customers")
      revalidateTag(customerCacheTag)
      return { success: true, error: null }
    })
    .catch((err) => {
      return { success: false, error: err.toString() }
    })
}

export const updateCustomerAddress = async (
  _currentState: AddressState,
  formData: FormData
): Promise<AddressState> => {
  const addressId = String(formData.get("addressId") ?? "")
  const { errors, address } = readAddressForm(formData)

  if (!addressId) {
    return { success: false, error: ADDRESS_FAILED }
  }

  if (Object.keys(errors).length) {
    return { success: false, error: null, fields: errors }
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  return sdk.store.customer
    .updateAddress(addressId, address, {}, headers)
    .then(async () => {
      const customerCacheTag = await getCacheTag("customers")
      revalidateTag(customerCacheTag)
      return { success: true, error: null }
    })
    .catch(() => {
      return { success: false, error: ADDRESS_FAILED }
    })
}
